import { useEffect, useMemo, useState } from "react";

import {
  useSaveTemplate,
  useTemplate,
  useTemplatePreview,
  type TemplatePreview,
  type TemplateVersion,
} from "../../../queries";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { VersionDiff } from "./VersionDiff";

const sampleData: Record<string, string> = {
  amount: "4,780",
  booking_url: "https://www.sirgroutjupiter.com/schedule/",
  booking_link: "https://www.sirgroutjupiter.com/schedule/",
  brand_name: "Sir Grout Jupiter",
  brand_short: "Sir Grout",
  brand_short_name: "Sir Grout",
  business_address: "Business mailing address",
  call_center_phone: "(772) 672-1758",
  callback_phone: "(772) 672-1758",
  customer_name: "Yvonne Parker",
  dashboard_base_url: "https://dashboard.example.com",
  days_or_weeks: "a few days",
  deadline_date: "May 31",
  discount: "10%",
  discount_usd: "75",
  display_phone: "(772) 672-1758",
  email: "yvonne@example.com",
  first_name: "Yvonne",
  gm_email: "sirgroutjupiter@gmail.com",
  gm_first_name: "Antonio",
  gm_name: "Antonio",
  gm_phone: "(772) 672-1758",
  google_review_url: "https://g.page/r/example/review",
  opt_out_text: "",
  phone: "(561) 555-0199",
  quote_age_human: "6 months ago",
  quote_id: "253667",
  referral_smart_link: "https://reviews.example.com/referral",
  reply_excerpt: "Yes, we would like to schedule.",
  room: "bathroom",
  sender_first: "Antonio",
  sender_first_name: "Antonio",
  sender_name: "Antonio",
  signal: "Accepted reply",
  smart_link: "https://reviews.example.com/r/abc123",
  star_links: "1 star: https://example.com?r=1\n2 stars: https://example.com?r=2\n3 stars: https://example.com?r=3\n4 stars: https://example.com?r=4\n5 stars: https://example.com?r=5",
  time_phrase: "a couple weeks back",
  total: "4,780",
  unsubscribe_url: "https://reviews.example.com/unsubscribe",
  website_review_url: "https://www.sirgroutjupiter.com/reviews/",
  year_last_service: "2024",
};

function extractVariables(...parts: string[]) {
  const names = new Set<string>();
  for (const part of parts) {
    const matches = part.matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g);
    for (const match of matches) names.add(match[1]);
  }
  return Array.from(names).sort();
}

export function TemplateEditor({ templateId }: { templateId: number | null }) {
  const detail = useTemplate(templateId);
  const save = useSaveTemplate(templateId);
  const preview = useTemplatePreview(templateId);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [compare, setCompare] = useState<TemplateVersion | null>(null);
  const [previewPayload, setPreviewPayload] = useState<TemplatePreview | null>(null);

  useEffect(() => {
    const current = detail.data?.current_version;
    setSubject(current?.subject || "");
    setBody(current?.body || "");
    setChangeNote("");
    setCompare(null);
  }, [detail.data?.current_version?.id]);

  const variables = useMemo(() => extractVariables(subject, body), [subject, body]);
  const disabled = !templateId || !body.trim() || save.isPending;

  if (!templateId) return <Card className="p-8 text-sm text-slate-500">Select a template to edit.</Card>;
  if (detail.isLoading) return <Card className="p-8 text-sm text-slate-500">Loading template...</Card>;
  if (!detail.data) return <Card className="p-8 text-sm text-red-600">Template unavailable.</Card>;

  function insertVariable(name: string) {
    setBody((value) => `${value}${value.endsWith(" ") || value.length === 0 ? "" : " "}{{${name}}}`);
  }

  async function runPreview() {
    const rendered = await preview.mutateAsync(sampleData);
    setPreviewPayload(rendered);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>{detail.data.name}</CardTitle>
            <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
              <span>{detail.data.channel.toUpperCase()} template</span>
              {detail.data.code ? <span className="font-mono">{detail.data.code}</span> : null}
            </div>
            {detail.data.purpose ? <div className="mt-1 text-xs text-slate-500">{detail.data.purpose}</div> : null}
          </div>
          <Badge variant="slate">v{detail.data.current_version?.version || 1}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {detail.data.channel === "email" ? (
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Subject</span>
            <Input className="mt-1" value={subject} onChange={(event) => setSubject(event.target.value)} />
          </label>
        ) : null}
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Body</span>
          <textarea
            className="mt-1 min-h-[520px] w-full rounded-md border border-slate-200 bg-white p-3 font-mono text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
        </label>
        <div className="flex flex-wrap gap-2">
          {(variables.length ? variables : Object.keys(sampleData).slice(0, 12)).map((name) => (
            <button key={name} type="button" className="rounded-full border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50" onClick={() => insertVariable(name)}>
              {`{{${name}}}`}
            </button>
          ))}
        </div>
        <Input placeholder="Change note" value={changeNote} onChange={(event) => setChangeNote(event.target.value)} />
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={runPreview}>Preview</Button>
          <Button
            disabled={disabled}
            onClick={() => save.mutate({ subject, body, change_note: changeNote })}
          >
            {save.isPending ? "Saving..." : "Save new version"}
          </Button>
        </div>
        {save.error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm leading-5 text-red-900">
            {save.error instanceof Error ? save.error.message : "Template save failed"}
          </div>
        ) : null}

        {previewPayload ? (
          <div className="mobile-dialog-backdrop">
            <div className="mobile-dialog-panel max-w-3xl p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-950">Template Preview</h3>
                <Button variant="ghost" onClick={() => setPreviewPayload(null)}>Close</Button>
              </div>
              {previewPayload.subject_rendered ? <div className="mt-4 rounded-md bg-slate-50 p-3 text-sm">{previewPayload.subject_rendered}</div> : null}
              <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap rounded-md bg-slate-950 p-4 text-sm text-white">{previewPayload.body_rendered}</pre>
              {previewPayload.missing_variables.length ? (
                <div className="mt-3 text-xs text-amber-700">Missing: {previewPayload.missing_variables.join(", ")}</div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="border-t border-slate-100 pt-4">
          <div className="mb-3 text-sm font-medium text-slate-950">Version history</div>
          <div className="mb-4 flex flex-wrap gap-2">
            {detail.data.versions.slice(1).map((version) => (
              <button key={version.id} className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50" type="button" onClick={() => setCompare(version)}>
                v{version.version}
              </button>
            ))}
          </div>
          <VersionDiff current={detail.data.current_version} compare={compare} />
        </div>
      </CardContent>
    </Card>
  );
}
